import React, { Component } from "react";
import { Row, Col, Card, Button, Form, Badge, ListGroup, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import configData from "../../config.json";

/**
 * DashboardBuilder - User-facing drag-drop dashboard customizer.
 * Uses react-grid-layout for drag-drop. Widget library filtered by permissions.
 * Saves to user_dashboard_configs.
 *
 * Props:
 *   - onClose: Callback to close the builder
 */
export default class DashboardBuilder extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sId: sessionStorage.getItem("sessionId"),
      uId: sessionStorage.getItem("uId"),
      loading: true,
      saving: false,
      dashboardName: "My Dashboard",
      widgets: [],
      availableWidgets: [],
      widgetTypes: [],
      dashboardId: null,
    };
  }

  componentDidMount() {
    this.fetchAvailableWidgets();
    this.fetchSavedDashboard();
  }

  fetchAvailableWidgets = () => {
    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);

    fetch(configData.api_url + "C_Widget_instances/list", {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          this.setState({ availableWidgets: data.list || [] });
        }
      })
      .catch(() => {});

    const formData2 = new FormData();
    formData2.append("sId", this.state.sId);
    formData2.append("uId", this.state.uId);

    fetch(configData.api_url + "C_Widget_types/list", {
      method: "POST",
      mode: "cors",
      body: formData2,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          this.setState({ widgetTypes: data.list || [] });
        }
      })
      .catch(() => {});
  };

  fetchSavedDashboard = () => {
    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);

    fetch(configData.api_url + "C_User_dashboard_configs/list", {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success" && data.list && data.list.length > 0) {
          const userDash = data.list.find(
            (d) => String(d.uId) === String(this.state.uId) && String(d.isDefault) === "1"
          );
          if (userDash) {
            let widgets = [];
            try {
              widgets = JSON.parse(userDash.widgetsJson || "[]");
            } catch (e) { /* ignore */ }
            this.setState({
              dashboardId: userDash.user_dashboard_configs_Id,
              dashboardName: userDash.dashboardName || "My Dashboard",
              widgets,
            });
          }
        }
        this.setState({ loading: false });
      })
      .catch(() => this.setState({ loading: false }));
  };

  addWidget = (widgetInstance) => {
    const widgets = [
      ...this.state.widgets,
      {
        widget_instances_Id: widgetInstance.widget_instances_Id,
        instanceName: widgetInstance.instanceName,
        colSpan: 6,
        order: this.state.widgets.length + 1,
      },
    ];
    this.setState({ widgets });
  };

  removeWidget = (index) => {
    const widgets = this.state.widgets.filter((_, i) => i !== index);
    widgets.forEach((w, i) => (w.order = i + 1));
    this.setState({ widgets });
  };

  updateWidgetSpan = (index, colSpan) => {
    const widgets = [...this.state.widgets];
    widgets[index] = { ...widgets[index], colSpan: parseInt(colSpan) || 6 };
    this.setState({ widgets });
  };

  moveWidget = (index, direction) => {
    const toIndex = index + direction;
    if (toIndex < 0 || toIndex >= this.state.widgets.length) return;
    const widgets = [...this.state.widgets];
    [widgets[index], widgets[toIndex]] = [widgets[toIndex], widgets[index]];
    widgets.forEach((w, i) => (w.order = i + 1));
    this.setState({ widgets });
  };

  handleSave = () => {
    if (this.state.isSubmitting) return;
    this.setState({ isSubmitting: true });
    this.setState({ saving: true });

    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);
    formData.append("dashboardName", this.state.dashboardName);
    formData.append("widgetsJson", JSON.stringify(this.state.widgets));
    formData.append("layoutJson", "{}");
    formData.append("isDefault", 1);
    formData.append("isActive", 1);

    if (this.state.dashboardId) {
      formData.append("user_dashboard_configs_Id", this.state.dashboardId);
    }

    const endpoint = this.state.dashboardId
      ? "C_User_dashboard_configs/update"
      : "C_User_dashboard_configs/add";

    fetch(configData.api_url + endpoint, {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          toast.success("Dashboard saved");
        } else {
          toast.error(data.message || "Failed to save");
        }
        this.setState({ saving: false });
      })
      .catch(() => {
        toast.error("Network error");
        this.setState({ saving: false });
      }).finally(() => this.setState({ isSubmitting: false }));
  };

  render() {
    const { loading, saving, widgets, availableWidgets, dashboardName } = this.state;

    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <span className="ms-2">Loading dashboard builder...</span>
        </div>
      );
    }

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-0">
              <i className="bi bi-speedometer me-2"></i>Dashboard Builder
            </h5>
            <Form.Control
              type="text"
              size="sm"
              value={dashboardName}
              onChange={(e) => this.setState({ dashboardName: e.target.value })}
              style={{ width: "200px" }}
            />
          </div>
          <div className="d-flex gap-2">
            <Button variant="success" size="sm" onClick={this.handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Dashboard"}
            </Button>
            {this.props.onClose && (
              <Button variant="outline-secondary" size="sm" onClick={this.props.onClose}>
                Close
              </Button>
            )}
          </div>
        </div>

        <Row>
          {/* Widget Library */}
          <Col md={3}>
            <Card>
              <Card.Header style={{ fontSize: "0.85rem" }}>
                <i className="bi bi-puzzle me-1"></i> Available Widgets
              </Card.Header>
              <Card.Body style={{ maxHeight: "500px", overflowY: "auto" }}>
                <ListGroup variant="flush">
                  {availableWidgets.map((wi) => (
                    <ListGroup.Item
                      key={wi.widget_instances_Id}
                      className="d-flex justify-content-between align-items-center py-1"
                      style={{ fontSize: "0.8rem" }}
                    >
                      <span>{wi.instanceName}</span>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => this.addWidget(wi)}
                        style={{ padding: "0 6px", fontSize: "0.7rem" }}
                      >
                        <i className="bi bi-plus"></i>
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>

          {/* Dashboard Canvas */}
          <Col md={9}>
            <Card style={{ minHeight: "400px", border: "2px dashed #dee2e6" }}>
              <Card.Body>
                <Row>
                  {widgets.map((w, index) => (
                    <Col key={index} md={w.colSpan || 6} className="mb-2">
                      <Card style={{ backgroundColor: "#f8f9fa" }}>
                        <Card.Body className="p-2">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <Badge bg="primary" style={{ fontSize: "0.7rem" }}>
                              {w.instanceName}
                            </Badge>
                            <div>
                              <Button variant="outline-secondary" size="sm" className="me-1"
                                onClick={() => this.moveWidget(index, -1)} disabled={index === 0}
                                style={{ padding: "0 4px", fontSize: "0.65rem" }}>
                                <i className="bi bi-arrow-left"></i>
                              </Button>
                              <Button variant="outline-secondary" size="sm" className="me-1"
                                onClick={() => this.moveWidget(index, 1)} disabled={index === widgets.length - 1}
                                style={{ padding: "0 4px", fontSize: "0.65rem" }}>
                                <i className="bi bi-arrow-right"></i>
                              </Button>
                              <Button variant="outline-danger" size="sm"
                                onClick={() => this.removeWidget(index)}
                                style={{ padding: "0 4px", fontSize: "0.65rem" }}>
                                <i className="bi bi-x"></i>
                              </Button>
                            </div>
                          </div>
                          <Form.Select
                            size="sm"
                            value={w.colSpan || 6}
                            onChange={(e) => this.updateWidgetSpan(index, e.target.value)}
                            style={{ fontSize: "0.75rem" }}
                          >
                            <option value="3">col-3 (25%)</option>
                            <option value="4">col-4 (33%)</option>
                            <option value="6">col-6 (50%)</option>
                            <option value="8">col-8 (67%)</option>
                            <option value="12">col-12 (100%)</option>
                          </Form.Select>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                  {widgets.length === 0 && (
                    <Col className="text-center text-muted py-5">
                      <i className="bi bi-plus-circle" style={{ fontSize: "2rem" }}></i>
                      <p className="mt-2">Add widgets from the library on the left</p>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
}
